import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { startHomePage } from "../../store/publicEvents";
import { getCategoryWithEvents } from "../../api/providers";

import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { PublicLayout } from "../../layouts";
import { EventCard, HeroSlider } from "../../components/public";
import { LoadingScreen } from "../../ui/components/LoadingScreen";

import { UpcomingEventInterface } from "../../interfaces";
import { loadingError, loadCategoryEvents } from "../../store/publicEvents";

export const HomePage = () => {
  const [selectedCategory, setSelectedCategory] = React.useState<string>("all");

  const {
    status,
    categoriesWithEvents,
    upcomingEvents,
    featuredEvents,
    categories,
  } = useSelector((state: RootState) => state.publicEvents);

  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    dispatch(startHomePage());
  }, [dispatch]);

  useEffect(() => {
    if (selectedCategory !== "all") {
      const fetchCategoryEvents = async () => {
        const categoryWithEvents = await getCategoryWithEvents(
          selectedCategory
        );
        if (categoryWithEvents.ok) {
          dispatch(loadCategoryEvents(categoryWithEvents.categoryWithEvents));
        } else {
          dispatch(
            loadingError({
              errorMessage: categoryWithEvents.errorMessage.error,
            })
          );
        }
      };
      fetchCategoryEvents();
    }
  }, [selectedCategory, dispatch]);

  if (status === "loading") {
    return <LoadingScreen />;
  }

  const handleCategoryChange = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setSelectedCategory(event.target.value as string);
  };

  const eventsToDisplay: UpcomingEventInterface[] =
    selectedCategory === "all" ? upcomingEvents : categoriesWithEvents || [];

  return (
    <PublicLayout>
      <HeroSlider featuredEvents={featuredEvents} />

      <Box mt={5} mx={{xs:2, md:7}}>
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 4 }}
        >
          <Grid item>
            <Typography
              component={"h2"}
              variant="h4"
              fontWeight={"600"}
              mb={2}
              sx={{
                fontSize: { xs: "1.5rem", sm: "2.2rem" },
                fontFamily: "Baloo 2",
              }}
            >
              Próximos eventos
            </Typography>
          </Grid>

          <Grid item>
            <FormControl
              variant="outlined"
              sx={{ minWidth: 200, color: "primary.main" }}
            >
              <InputLabel
                id="category-select-label"
                sx={{
                  color: "primary.main",
                  "&.Mui-focused": { color: "primary.main" },
                  "&.MuiInputLabel-shrink": { color: "primary.main" },
                }}
              >
                Categorías
              </InputLabel>
              <Select
                labelId="category-select-label"
                id="category-select"
                value={selectedCategory}
                onChange={handleCategoryChange}
                label="Categoría"
                displayEmpty
                sx={{
                  backgroundColor: "darkBackground.light",
                  color: "white",
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.main",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.main",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "primary.main",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "primary.main",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "darkBackground.light",
                      "& .MuiMenuItem-root": {
                        color: "white",
                        "&:hover": {
                          backgroundColor: "darkBackground.dark",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "darkBackground.dark",
                          "&:hover": {
                            backgroundColor: "darkBackground.light",
                          },
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem
                  value="all"
                  sx={{ "&:hover": { backgroundColor: "darkBackground.dark" } }}
                >
                  Todas las categorías
                </MenuItem>
                {categories.map(
                  (category: { id: number; name: string }, index: number) => (
                    <MenuItem
                      key={category.id}
                      value={category.id}
                      sx={{
                        "&:hover": { backgroundColor: "darkBackground.dark" },
                      }}
                    >
                      {category.name}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>
          </Grid>

          <Grid container sx={{ mt: { xs: 0, sm: 5 } }}>
            <Grid
              container
              justifyContent={"center"}
              spacing={{xs:2, md: 5}}
              sx={{ mt: { xs: 1, sm: 0 }, mb: 5 }}
            >
              {eventsToDisplay.map((event, eventIndex) => (
                <Grid item xs={6} md={4} lg={3} key={eventIndex}>
                  <EventCard
                    name={event.name}
                    startDate={event.startDate}
                    address={event.address}
                    banner={event.banner}
                    category={event.category}
                    id={event.id}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </PublicLayout>
  );
};
